<template>
  <b-modal
    id="modal-criar-cliente"
    title="Novo Cliente"
    size="xl"
    no-close-on-backdrop
    no-close-on-esc
    @hidden="resetForm"
    @show="carregarOpcoes"
  >
    <b-tabs v-model="tabAtiva" pills card>

      <!-- ===================== ABA 1: DADOS BÁSICOS ===================== -->
      <b-tab title="1. Dados Básicos" :title-link-class="tabErro[0] ? 'text-danger' : ''">
        <validation-observer ref="formObserver">
          <b-form @submit.prevent>
            <b-row>
              <b-col cols="12" class="mb-1">
                <h6 class="text-primary font-weight-bold">Identificação</h6>
                <hr class="mt-0" />
              </b-col>

              <b-col cols="12" md="3" class="mb-1">
                <b-form-group label="Código ERP (automático)" label-for="cc-erp">
                  <!-- Gerado automaticamente no backend ao salvar (prefixo 9 = fora do range TOTVS) -->
                  <b-form-input
                    id="cc-erp"
                    v-model="form.clienteiderp"
                    placeholder="Gerado ao salvar"
                    readonly
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" md="3" class="mb-1">
                <b-form-group label="Tipo Pessoa *" label-for="cc-tipo">
                  <validation-provider #default="{ errors }" name="Tipo Pessoa" rules="required">
                    <v-select
                      id="cc-tipo"
                      v-model="form.clientetipopessoa"
                      :options="tipoPessoaOpts"
                      :reduce="o => o.value"
                      label="label"
                      placeholder="Selecione..."
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6" class="mb-1">
                <b-form-group label="Nome Abreviado *" label-for="cc-abr">
                  <validation-provider #default="{ errors }" name="Nome Abreviado" rules="required">
                    <b-form-input
                      id="cc-abr"
                      v-model="form.clientenomeabreviado"
                      placeholder="Nome curto"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6" class="mb-1">
                <b-form-group label="Razão Social *" label-for="cc-razao">
                  <validation-provider #default="{ errors }" name="Razão Social" rules="required">
                    <b-form-input
                      id="cc-razao"
                      v-model="form.clienterazaosocial"
                      placeholder="Razão social completa"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6" class="mb-1">
                <b-form-group label="Nome Fantasia" label-for="cc-fantasia">
                  <b-form-input
                    id="cc-fantasia"
                    v-model="form.clientenomefantasia"
                    placeholder="Nome fantasia"
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" class="mb-1 mt-1">
                <h6 class="text-primary font-weight-bold">Documento Fiscal</h6>
                <hr class="mt-0" />
              </b-col>

              <b-col cols="12" md="4" class="mb-1">
                <b-form-group label="CNPJ / CPF *" label-for="cc-cnpj">
                  <validation-provider #default="{ errors }" name="CNPJ / CPF" rules="required">
                    <b-form-input
                      id="cc-cnpj"
                      v-model="form.clientecnpj"
                      placeholder="00.000.000/0000-00"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col cols="12" md="4" class="mb-1">
                <b-form-group label="Inscrição Estadual" label-for="cc-ie">
                  <b-form-input
                    id="cc-ie"
                    v-model="form.clienteie"
                    placeholder="IE"
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" md="4" class="mb-1 d-flex align-items-center" style="padding-top:18px">
                <b-form-checkbox v-model="form.clienteisentoie" switch>
                  Isento de IE
                </b-form-checkbox>
              </b-col>

              <!-- Seção "Comercial" removida a pedido do cliente: representante
                   não possui essas informações (canal, crédito). Os campos
                   continuam no form/data com defaults e vão vazios no payload
                   (backend associa padrões automaticamente). -->
              <b-col cols="12" class="mb-1">
                <b-form-group label="Observação" label-for="cc-obs">
                  <b-form-textarea
                    id="cc-obs"
                    v-model="form.clienteobservacao"
                    rows="2"
                    placeholder="Observações gerais..."
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-tab>

      <!-- ===================== ABA 2: ENDEREÇO ===================== -->
      <b-tab title="2. Endereço">
        <b-row>
          <b-col cols="12" class="mb-1">
            <h6 class="text-primary font-weight-bold">Endereço do Cliente *</h6>
            <small class="text-muted">Obrigatório — preencha tipo, logradouro, número, CEP, cidade, bairro e UF.</small>
            <hr class="mt-1" />
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Tipo de Endereço *" label-for="en-tipo">
              <v-select
                id="en-tipo"
                v-model="endereco.tipoenderecoid"
                :options="tipoEnderecoOpts"
                :reduce="o => o.value"
                label="label"
                placeholder="Selecione..."
                :loading="carregando.tipoEndereco"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="5" class="mb-1">
            <b-form-group label="Logradouro *" label-for="en-logradouro">
              <b-form-input
                id="en-logradouro"
                v-model="endereco.clienteendereco"
                placeholder="Rua, Avenida..."
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <b-form-group label="Número *" label-for="en-numero">
              <b-form-input
                id="en-numero"
                v-model="endereco.clienteendereconumero"
                placeholder="Ex: 100"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Complemento" label-for="en-comp">
              <b-form-input
                id="en-comp"
                v-model="endereco.clienteenderecocomplemento"
                placeholder="Sala, Bloco..."
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <b-form-group label="CEP *" label-for="en-cep">
              <b-form-input
                id="en-cep"
                v-model="endereco.clienteenderecocep"
                placeholder="00000-000"
                @blur="consultarCepEndereco"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="5" class="mb-1">
            <b-form-group label="Cidade *" label-for="en-cidade">
              <b-form-input
                id="en-cidade"
                v-model="endereco.clienteenderecocidade"
                placeholder="Cidade"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Bairro *" label-for="en-bairro">
              <b-form-input
                id="en-bairro"
                v-model="endereco.clienteenderecobairro"
                placeholder="Bairro"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="2" class="mb-1">
            <b-form-group label="UF *" label-for="en-uf">
              <v-select
                id="en-uf"
                v-model="endereco.ufid"
                :options="ufOpts"
                placeholder="UF"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <b-form-group label="País ID" label-for="en-pais">
              <b-form-input
                id="en-pais"
                v-model="endereco.paisid"
                type="number"
                placeholder="76"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <b-form-group label="IE do Endereço" label-for="en-ie">
              <b-form-input
                id="en-ie"
                v-model="endereco.clienteenderecoie"
                placeholder="IE"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" class="mb-1">
            <b-form-group label="Observações" label-for="en-obs">
              <b-form-textarea
                id="en-obs"
                v-model="endereco.clienteenderecoobservacoes"
                rows="2"
                placeholder="Observações do endereço..."
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-tab>

      <!-- Aba "Condições e Tabelas" removida: a condição de pagamento e a tabela
           de preço padrão são associadas automaticamente pelo backend ao salvar. -->
    </b-tabs>

    <!-- ===================== PROGRESSO DE SALVAMENTO ===================== -->
    <b-alert v-if="progressoSalvamento.length" show variant="light" class="mt-1 p-1">
      <div v-for="(p, i) in progressoSalvamento" :key="i" class="d-flex align-items-center mb-25">
        <b-spinner v-if="p.status === 'loading'" small class="mr-50" />
        <feather-icon v-else-if="p.status === 'ok'" icon="CheckCircleIcon" size="16" class="text-success mr-50" />
        <feather-icon v-else-if="p.status === 'erro'" icon="XCircleIcon" size="16" class="text-danger mr-50" />
        <span :class="{ 'text-success': p.status === 'ok', 'text-danger': p.status === 'erro' }">{{ p.msg }}</span>
      </div>
    </b-alert>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="tabAtiva = Math.max(0, tabAtiva - 1)" :disabled="tabAtiva === 0 || salvando">
        Anterior
      </b-button>
      <b-button v-if="tabAtiva < 1" variant="primary" @click="avancar">
        Próximo
      </b-button>
      <b-button variant="secondary" @click="$bvModal.hide('modal-criar-cliente')" :disabled="salvando">
        Cancelar
      </b-button>
      <b-button variant="success" :disabled="salvando" @click="salvar">
        <b-spinner v-if="salvando" small class="mr-1" />
        {{ salvando ? 'Salvando...' : 'Salvar' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BForm, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox,
  BFormTextarea, BButton, BSpinner, BTabs, BTab, BAlert, BBadge,
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { required } from '@validations';
import vSelect from 'vue-select';
import axios from '@/libs/axios';
import SLSClienteService from '@/services/Cadastros/Cliente/SLSCliente.service';

export default {
  name: 'ModalCriarCliente',
  components: {
    BModal, BForm, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox,
    BFormTextarea, BButton, BSpinner, BTabs, BTab, BAlert, BBadge,
    ValidationObserver, ValidationProvider,
    vSelect,
  },
  data() {
    return {
      tabAtiva: 0,
      salvando: false,
      tabErro: [false, false, false, false],
      progressoSalvamento: [],
      form: this.emptyForm(),
      endereco: this.emptyEndereco(),
      service: new SLSClienteService(),
      required,
      tipoEnderecoOpts: [],
      carregando: { tipoEndereco: false },
      tipoPessoaOpts: [
        { label: 'Jurídica (J)', value: 'J' },
        { label: 'Física (F)', value: 'F' },
      ],
      tipoCreditoOpts: [
        { label: 'Cliente', value: 'Cliente' },
        { label: 'Bloqueado', value: 'Bloqueado' },
        { label: 'Suspenso', value: 'Suspenso' },
      ],
      ufOpts: [
        'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
        'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
      ],
    };
  },
  methods: {
    emptyForm() {
      return {
        clienteiderp: '',
        clientetipopessoa: 'J',
        clientenomeabreviado: '',
        clienterazaosocial: '',
        clientenomefantasia: '',
        clientecnpj: '',
        clienteisentoie: false,
        clienteie: '',
        canalvendaid: '',
        clientetipocredito: 'Cliente',
        clientevalorcredito: 0,
        clienteobservacao: '',
        clientesituacao: 'DIGITACAO',
        clientesituacaointegracao: 'NAO_INTEGRADO',
      };
    },
    emptyEndereco() {
      return {
        tipoenderecoid: null,
        clienteendereco: '',
        clienteendereconumero: '',
        clienteenderecocomplemento: '',
        clienteenderecocep: '',
        clienteenderecocidade: '',
        clienteenderecobairro: '',
        ufid: null,
        paisid: 76,
        clienteenderecoie: '',
        clienteenderecoobservacoes: '',
      };
    },
    resetForm() {
      this.tabAtiva = 0;
      this.form = this.emptyForm();
      this.endereco = this.emptyEndereco();
      this.salvando = false;
      this.progressoSalvamento = [];
      this.tabErro = [false, false, false, false];
      if (this.$refs.formObserver) this.$refs.formObserver.reset();
    },
    async carregarOpcoes() {
      // Código ERP é gerado no backend, com verificação de unicidade, ao salvar.
      this.form.clienteiderp = '';
      await this.carregarTipoEndereco();
    },
    async consultarCepEndereco() {
      const ViaCEPService = (await import('@/services/ViaCEP.service')).default;
      if (!ViaCEPService.isValidCepFormat(this.endereco.clienteenderecocep)) return;
      try {
        const data = await ViaCEPService.buscarCep(this.endereco.clienteenderecocep);
        if (data.logradouro) this.endereco.clienteendereco = data.logradouro;
        if (data.bairro) this.endereco.clienteenderecobairro = data.bairro;
        if (data.cidade) this.endereco.clienteenderecocidade = data.cidade;
        if (data.uf) this.endereco.ufid = String(data.uf).toUpperCase();
      } catch (err) {
        console.warn('[ViaCEP]', err && err.message ? err.message : err);
      }
    },
    async carregarTipoEndereco() {
      this.carregando.tipoEndereco = true;
      try {
        const { data } = await axios.get('/tipoEndereco');
        const lista = Array.isArray(data) ? data : (data.data || []);
        this.tipoEnderecoOpts = lista.map(t => ({
          label: `${t.tipoenderecodescricao || t.tipoenderecoid} (${t.tipoenderecoid})`,
          value: t.tipoenderecoid,
        }));
      } catch (e) {
        this.tipoEnderecoOpts = [
          { label: 'Padrão (PADRAO)', value: 'PADRAO' },
          { label: 'Cobrança (COBRANCA)', value: 'COBRANCA' },
          { label: 'Entrega (ENTREGA)', value: 'ENTREGA' },
        ];
      } finally {
        this.carregando.tipoEndereco = false;
      }
    },
    filtrarOpcao(option, label, search) {
      return label.toLowerCase().includes(search.toLowerCase());
    },
    avancar() {
      this.tabAtiva = Math.min(1, this.tabAtiva + 1);
    },
    adicionarProgresso(msg, status = 'loading') {
      this.progressoSalvamento.push({ msg, status });
      return this.progressoSalvamento.length - 1;
    },
    atualizarProgresso(idx, status, msg) {
      if (msg) this.progressoSalvamento[idx].msg = msg;
      this.progressoSalvamento[idx].status = status;
      this.$forceUpdate();
    },
    async salvar() {
      const valid = await this.$refs.formObserver.validate();
      if (!valid) {
        this.tabAtiva = 0;
        this.$set(this.tabErro, 0, true);
        this.$bvToast.toast('Corrija os campos obrigatórios na aba "Dados Básicos".', {
          title: 'Atenção', variant: 'warning', solid: true,
        });
        return;
      }
      this.$set(this.tabErro, 0, false);
      // Endereço obrigatório: valida os campos essenciais da aba 2.
      const e = this.endereco;
      const enderecoOk = e.tipoenderecoid && String(e.clienteendereco || '').trim()
        && String(e.clienteendereconumero || '').trim() && String(e.clienteenderecocep || '').trim()
        && String(e.clienteenderecocidade || '').trim() && String(e.clienteenderecobairro || '').trim()
        && e.ufid;
      if (!enderecoOk) {
        this.tabAtiva = 1;
        this.$set(this.tabErro, 1, true);
        this.$bvToast.toast('Preencha o endereço do cliente (tipo, logradouro, número, CEP, cidade, bairro e UF).', {
          title: 'Atenção', variant: 'warning', solid: true,
        });
        return;
      }
      this.$set(this.tabErro, 1, false);
      this.salvando = true;
      this.progressoSalvamento = [];

      let clienteIdCriado = null;
      let clienteIdErpCriado = null;

      try {
        // 1. Criar Cliente (código ERP é gerado no backend com verificação de unicidade)
        const idxCliente = this.adicionarProgresso('Criando cliente...');
        const resp = await this.service.store(this.form);
        if (!resp) {
          this.atualizarProgresso(idxCliente, 'erro', 'Erro ao criar cliente — verifique os dados');
          throw new Error('Falha ao criar cliente');
        }
        clienteIdCriado = resp?.clienteid || resp?.data?.clienteid;
        clienteIdErpCriado = resp?.clienteiderp || resp?.data?.clienteiderp;
        this.form.clienteiderp = clienteIdErpCriado || '';
        this.atualizarProgresso(idxCliente, 'ok', `Cliente criado (ID: ${clienteIdCriado || '?'})`);


        // 2. Criar Endereço (obrigatório — validado antes do POST do cliente)
        let endOk = false;
        const idxEnd = this.adicionarProgresso('Salvando endereço...');
        try {
          await axios.post('/cliente-endereco', {
            ...this.endereco,
            clienteid: clienteIdCriado,
            clienteiderp: clienteIdErpCriado,
          });
          endOk = true;
          this.atualizarProgresso(idxEnd, 'ok', 'Endereço salvo');
        } catch (e) {
          this.atualizarProgresso(idxEnd, 'erro', `Endereço não salvo: ${e?.response?.data?.message || e.message}`);
        }

        // Condição de pagamento e tabela de preço padrão são associadas
        // automaticamente pelo backend ao criar o cliente.
        this.$emit('criado');

        if (!endOk) {
          // Cliente foi criado, mas ficou sem endereço — não fechar o modal
          // nem sinalizar sucesso total.
          this.$bvToast.toast(
            `Cliente ${clienteIdErpCriado || ''} criado, mas o endereço não foi salvo. Complete o cadastro pela tela de edição do cliente.`,
            { title: 'Atenção', variant: 'warning', solid: true, noAutoHide: true },
          );
          return;
        }

        this.$bvToast.toast('Cliente criado com sucesso!', {
          title: 'Sucesso', variant: 'success', solid: true,
        });

        setTimeout(() => {
          this.$bvModal.hide('modal-criar-cliente');
        }, 1200);

      } catch (e) {
        this.$bvToast.toast('Erro ao criar cliente. Verifique os dados.', {
          title: 'Erro', variant: 'danger', solid: true,
        });
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>
